import { Button, Flex, Stack, Text, Textarea } from '@mantine/core'
import { IconSend } from '@tabler/icons-react'
import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
	CourseAiLearnerRow,
	CourseAiSupportConversation,
	CourseAiSupportMessage,
	getLearnerSupportMessages,
	openLearnerSupportConversation,
	sendLearnerSupportMessage,
	unwrapObject,
} from 'src/apis/courseAiAPI'
import CustomDrawer from 'src/components/CustomComponents/CustomDrawer'
import { formatDateTime } from './learnerDisplay'

const PAGE_LIMIT = 50
const MAX_LENGTH = 5000
/** The CMS has no socket, so the open conversation is re-read on a timer. */
const POLL_MS = 5000

const errorMessage = (error: any, fallback: string) =>
	error?.response?.data?.message ||
	error?.response?.data?.results?.message ||
	fallback

const messageText = (message: CourseAiSupportMessage) =>
	message.content || (message.medias?.length ? '(attachment)' : '-')

/**
 * The learner's Course AI support conversation, opened from the chat icon of a
 * row. The server reads and sends as the support account; the names shown are
 * the ones it returns.
 */
function LearnerSupportChatDrawer({
	learner,
	onClose,
}: {
	learner: CourseAiLearnerRow | null
	onClose: () => void
}) {
	const [conversation, setConversation] = useState<CourseAiSupportConversation | null>(null)
	const [messages, setMessages] = useState<CourseAiSupportMessage[]>([])
	const [loading, setLoading] = useState(false)
	const [draft, setDraft] = useState('')
	const [sending, setSending] = useState(false)
	const bottomRef = useRef<HTMLDivElement | null>(null)
	const userId = learner?.user_id || null

	useEffect(() => {
		setConversation(null)
		setMessages([])
		setDraft('')
		if (!userId) return undefined

		let active = true
		let timer: ReturnType<typeof setTimeout> | undefined

		const readMessages = async () => {
			const data = unwrapObject<{ rows: CourseAiSupportMessage[] }>(
				await getLearnerSupportMessages(userId, { page: 1, limit: PAGE_LIMIT }),
			)
			// The server answers newest first; the panel reads oldest first.
			if (active) setMessages([...(data?.rows || [])].reverse())
		}

		const poll = () => {
			timer = setTimeout(async () => {
				try {
					await readMessages()
				} catch (error) {
					// A missed poll is retried on the next tick.
				}
				if (active) poll()
			}, POLL_MS)
		}

		const open = async () => {
			setLoading(true)
			try {
				const opened = unwrapObject<CourseAiSupportConversation>(await openLearnerSupportConversation(userId))
				if (!active) return
				setConversation(opened)
				await readMessages()
				if (active) poll()
			} catch (error) {
				if (active) toast.error(errorMessage(error, 'Could not open the support chat'))
			} finally {
				if (active) setLoading(false)
			}
		}

		open()
		return () => {
			active = false
			if (timer) clearTimeout(timer)
		}
	}, [userId])

	const lastMessageId = messages.length ? messages[messages.length - 1].id : null
	useEffect(() => {
		bottomRef.current?.scrollIntoView({ block: 'end' })
	}, [lastMessageId])

	const handleSend = async () => {
		const content = draft.trim()
		if (!userId || !content) return
		setSending(true)
		try {
			const data = unwrapObject<{ message: CourseAiSupportMessage }>(await sendLearnerSupportMessage(userId, content))
			const sent = data?.message
			if (sent) {
				setMessages((previous) =>
					previous.some((message) => message.id === sent.id) ? previous : [...previous, sent],
				)
			}
			setDraft('')
		} catch (error) {
			toast.error(errorMessage(error, 'Could not send the message'))
		} finally {
			setSending(false)
		}
	}

	const supportName = conversation?.support?.name || ''

	return (
		<CustomDrawer
			opened={Boolean(learner)}
			onClose={onClose}
			loading={loading}
			title={
				<>
					<span style={{ display: 'block' }}>Support chat</span>
					<span style={{ display: 'block', fontSize: 14, fontWeight: 400, color: '#868e96' }}>
						{learner?.name || '-'}
						{learner?.course_name ? `, ${learner.course_name}` : ''}
					</span>
				</>
			}
			footer={
				<Stack spacing={8}>
					<Text size="xs" color="dimmed">
						{supportName ? `Sent as ${supportName}` : ' '}
					</Text>
					<Flex gap={8} align="flex-end">
						<Textarea
							placeholder="Write a message"
							autosize
							minRows={1}
							maxRows={5}
							maxLength={MAX_LENGTH}
							value={draft}
							onChange={(event) => setDraft(event.currentTarget.value)}
							sx={{ flex: 1 }}
						/>
						<Button
							color="green"
							leftIcon={<IconSend size={16} />}
							loading={sending}
							disabled={!draft.trim() || !conversation}
							onClick={handleSend}
						>
							Send
						</Button>
					</Flex>
				</Stack>
			}
		>
			<Stack spacing={12} sx={{ padding: '16px 18px' }}>
				{!loading && messages.length === 0 ? (
					<Text size="sm" color="dimmed">
						No messages yet.
					</Text>
				) : null}
				{messages.map((message) => {
					const fromSupport = message.from === 'support'
					return (
						<Stack key={message.id} spacing={2} align={fromSupport ? 'flex-end' : 'flex-start'}>
							<Text size="xs" color="dimmed">
								{message.sender?.name || (fromSupport ? supportName : learner?.name) || '-'}
								{' · '}
								{formatDateTime(message.created_at)}
							</Text>
							<Text
								size="sm"
								sx={{
									maxWidth: '82%',
									padding: '8px 12px',
									borderRadius: 10,
									whiteSpace: 'pre-wrap',
									wordBreak: 'break-word',
									background: fromSupport ? '#ecfdf3' : '#f7faf7',
									border: `1px solid ${fromSupport ? '#cfe4d6' : '#e6ebe7'}`,
									color: '#18241b',
								}}
							>
								{messageText(message)}
							</Text>
						</Stack>
					)
				})}
				<div ref={bottomRef} />
			</Stack>
		</CustomDrawer>
	)
}

export default LearnerSupportChatDrawer
